import { useMemo, useState } from "react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday } from "date-fns";
import { ko } from "date-fns/locale";
import { RefreshCw, Users, MapPin, Calendar, ChevronRight, Sparkles, Clock, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePerformanceTracking } from "@/lib/performance-monitor";
import { useMonthlyTourSchedule, type MonthlyTourScheduleItem } from "@/hooks/useMonthlyTourSchedule";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const CompactTourCalendar = () => {
  usePerformanceTracking('CompactTourCalendar');
  const queryClient = useQueryClient();
  
  const { data: tours, isLoading, error, isSuccess, isFetching, failureCount } = useMonthlyTourSchedule();
  
  // 확장된 날짜들을 추적하는 상태
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  // 현재 날짜가 속한 주의 일요일부터 4주간 날짜 생성
  const { calendarDays, dateRange } = useMemo(() => {
    const today = new Date();
    
    // 이번 주 일요일 찾기
    const dayOfWeek = getDay(today);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);
    
    // 4주 후 토요일까지
    const endDate = new Date(startOfWeek);
    endDate.setDate(startOfWeek.getDate() + 27); // 4주 = 28일 - 1
    
    // 날짜 배열 생성
    const days = eachDayOfInterval({ start: startOfWeek, end: endDate });
    
    // 날짜 범위 텍스트
    const rangeText = `${format(startOfWeek, 'M.d')} - ${format(endDate, 'M.d')}`;
    
    return { calendarDays: days, dateRange: rangeText };
  }, []);

  // 날짜별로 투어 그룹핑
  const groupedTours = useMemo(() => {
    if (!tours || tours.length === 0) return {};
    
    return tours.reduce((groups, tour) => {
      const dateKey = format(parseISO(tour.departure_date), 'yyyy-MM-dd');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(tour);
      return groups;
    }, {} as Record<string, MonthlyTourScheduleItem[]>);
  }, [tours]);

  // 수동 새로고침 핸들러
  const handleRefresh = () => {
    console.log('🔄 월간 투어 일정 수동 새로고침 시도');
    queryClient.invalidateQueries({ queryKey: ['monthly-tour-schedule'] });
  };
  
  // 날짜 확장/축소 토글
  const toggleDateExpansion = (dateKey: string) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  // 투어 상태 판별 (더 세분화)
  const getTourStatus = (tour: MonthlyTourScheduleItem) => {
    // status를 기반으로 상태 그룹 결정
    const recruitingStatuses = ['open', 'pending'];
    const confirmedStatuses = ['confirmed', 'ready'];
    
    if (confirmedStatuses.includes(tour.status || '')) {
      return 'confirmed';
    } else if (recruitingStatuses.includes(tour.status || '')) {
      if (tour.available_seats <= 5) {
        return 'recruiting-urgent';
      }
      return 'recruiting';
    }
    return 'available';
  };

  // 투어 상태별 스타일 정의 (2024 트렌드 반영)
  const getTourStatusStyle = (status: string) => {
    const styles = {
      'recruiting': {
        container: 'bg-gradient-to-br from-emerald-50/90 via-green-50/80 to-teal-50/90 backdrop-blur-sm border border-emerald-300/60 shadow-sm hover:shadow-emerald-400/30',
        badge: 'bg-emerald-500 text-white',
        icon: '🚀',
        pulse: true,
        glow: 'shadow-emerald-500/20'
      },
      'recruiting-urgent': {
        container: 'bg-gradient-to-br from-amber-50/90 via-yellow-50/80 to-orange-50/90 backdrop-blur-sm border border-amber-300/60 shadow-sm hover:shadow-amber-400/30',
        badge: 'bg-amber-500 text-white',
        icon: '⚡',
        pulse: true,
        glow: 'shadow-amber-500/30 animate-pulse'
      },
      'confirmed': {
        container: 'bg-gradient-to-br from-blue-50/90 via-indigo-50/80 to-violet-50/90 backdrop-blur-sm border border-blue-300/60 shadow-sm hover:shadow-blue-400/30',
        badge: 'bg-blue-500 text-white',
        icon: '✅',
        pulse: false,
        glow: 'shadow-blue-500/20'
      },
      'available': {
        container: 'bg-gradient-to-br from-gray-50/90 via-slate-50/80 to-gray-50/90 backdrop-blur-sm border border-gray-300/60 shadow-sm hover:shadow-gray-400/30',
        badge: 'bg-gray-500 text-white',
        icon: '📅',
        pulse: false,
        glow: 'shadow-gray-500/20'
      }
    };
    return styles[status] || styles.available;
  };

  // 둘레길별 아이콘과 색상 매핑
  const getTrailStyle = (courseName: string) => {
    if (courseName.includes('해파랑길')) {
      return { color: 'text-blue-600', bg: 'bg-blue-100/80', icon: '🌊' };
    } else if (courseName.includes('남파랑길')) {
      return { color: 'text-orange-600', bg: 'bg-orange-100/80', icon: '🌅' };
    } else if (courseName.includes('서해랑길')) {
      return { color: 'text-green-600', bg: 'bg-green-100/80', icon: '🌿' };
    } else if (courseName.includes('DMZ') || courseName.includes('평화의길')) {
      return { color: 'text-purple-600', bg: 'bg-purple-100/80', icon: '☮️' };
    }
    return { color: 'text-gray-600', bg: 'bg-gray-100/80', icon: '🥾' };
  };

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100/80 overflow-hidden">
        <div className="text-center py-12 px-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100/70 border border-red-200/80 rounded-2xl p-6 inline-block shadow-sm">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-red-700 font-semibold text-lg mb-2">투어 일정을 불러오는 데 실패했습니다.</p>
            <p className="text-sm text-red-600 mb-4 max-w-md">{error.message}</p>
            {failureCount > 0 && (
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="mt-2 border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 transition-all duration-200 hover:scale-105"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                다시 시도
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
        {/* 로딩 스켈레톤 (shimmer effect) */}
        <div className="relative">
          {/* Shimmer 애니메이션 오버레이 */}
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          
          {/* 헤더 스켈레톤 */}
          <div className="bg-gradient-to-r from-gray-50/50 to-gray-100/50 px-6 py-5 border-b border-gray-200/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-8 w-32 bg-gray-200/60 rounded-xl animate-pulse" />
                <div className="h-6 w-20 bg-gray-200/60 rounded-full animate-pulse" />
              </div>
              <div className="hidden md:flex items-center gap-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-gray-200/40 rounded-xl animate-pulse">
                    <div className="w-8 h-8 bg-gray-300/60 rounded-lg" />
                    <div className="h-3 w-16 bg-gray-300/60 rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* 요일 스켈레톤 */}
          <div className="grid grid-cols-7 bg-gradient-to-b from-white/80 to-gray-50/30 border-b border-gray-200/40">
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} className="text-center py-2 md:py-3">
                <div className="h-3 md:h-4 w-3 md:w-4 bg-gray-200/60 rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
          
          {/* 캘린더 그리드 스켈레톤 */}
          <div className="grid grid-cols-7 bg-white/95">
            {Array.from({ length: 28 }, (_, i) => (
              <div key={i} className="min-h-[100px] md:min-h-[130px] lg:min-h-[150px] border-r border-b border-gray-200/40 p-2 md:p-3">
                <div className="h-5 md:h-6 w-5 md:w-6 bg-gray-200/60 rounded-full mb-2 animate-pulse" />
                <div className="space-y-1">
                  <div className="h-5 md:h-6 bg-gray-200/40 rounded-lg animate-pulse" />
                  {i % 3 === 0 && (
                    <div className="h-5 md:h-6 bg-gray-200/30 rounded-lg animate-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
      {/* 모던 캘린더 헤더 (2024 트렌드) */}
      <div className="relative bg-gradient-to-r from-white via-gray-50/50 to-white backdrop-blur-md px-6 py-5 border-b border-gray-200/30">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-purple-50/20 to-pink-50/20 opacity-40" />
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* 날짜 표시 */}
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <h5 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                {dateRange}
              </h5>
            </div>
            
            {/* 배지 */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full border border-blue-200/40 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-sm font-semibold text-gray-700">투어 일정</span>
            </div>
          </div>
          
          {/* 개선된 범례 (2024 스타일) */}
          <div className="flex items-center gap-3">
            {/* 상태별 그룹 */}
            <div className="flex items-center gap-4 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-sm">
              {/* 모집중 그룹 */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center shadow-sm">
                    <span className="text-xs">🚀</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-700">모집중</span>
                  <span className="text-[10px] text-gray-500">예약가능</span>
                </div>
              </div>
              
              {/* 구분선 */}
              <div className="w-px h-8 bg-gray-200" />
              
              {/* 출발확정 그룹 */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-violet-500 rounded-lg flex items-center justify-center shadow-sm">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-700">출발확정</span>
                  <span className="text-[10px] text-gray-500">준비완료</span>
                </div>
              </div>
              
              {/* 구분선 */}
              <div className="w-px h-8 bg-gray-200" />
              
              {/* 긴급 */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shadow-sm">
                    <span className="text-xs">⚡</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full animate-ping" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-700">마감임박</span>
                  <span className="text-[10px] text-gray-500">5석 이하</span>
                </div>
              </div>
            </div>
            
            {/* 새로고침 버튼 */}
            {isFetching && (
              <Button
                onClick={handleRefresh}
                variant="ghost"
                size="sm"
                className="rounded-xl hover:bg-gray-100/80 transition-all duration-300 hover:scale-105"
              >
                <RefreshCw className="w-4 h-4 animate-spin" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 요일 표시 (반응형) */}
      <div className="grid grid-cols-7 bg-gradient-to-b from-white/80 via-gray-50/50 to-white/80 backdrop-blur-sm border-b border-gray-200/40">
        {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
          <div 
            key={day} 
            className={cn(
              "text-center py-2 md:py-3 text-xs md:text-sm font-bold tracking-wide transition-colors",
              index === 0 && "text-red-500",
              index === 6 && "text-blue-500",
              index !== 0 && index !== 6 && "text-gray-700"
            )}
          >
            {/* 모바일에서는 첫 글자만 */}
            <span className="md:hidden">{day.charAt(0)}</span>
            <span className="hidden md:inline">{day}</span>
          </div>
        ))}
      </div>

      {/* 모던 캘린더 그리드 (글래스모피즘 + 반응형) */}
      <div className="grid grid-cols-7 bg-gradient-to-br from-white/95 via-gray-50/30 to-white/95 backdrop-blur-sm">
        {calendarDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayTours = groupedTours[dateKey] || [];
          const isCurrentDay = isToday(day);
          const dayOfWeek = getDay(day);
          const isPastDay = day < new Date(new Date().setHours(0, 0, 0, 0));
          
          // 투어를 상태별로 그룹핑
          const recruitingTours = dayTours.filter(t => ['open', 'pending'].includes(t.status || ''));
          const confirmedTours = dayTours.filter(t => ['confirmed', 'ready'].includes(t.status || ''));

          return (
            <div
              key={dateKey}
              className={cn(
                "min-h-[100px] md:min-h-[130px] lg:min-h-[150px] border-r border-b border-gray-200/40 p-2 md:p-3 transition-all duration-300 relative overflow-hidden group",
                isCurrentDay && "bg-gradient-to-br from-amber-50/70 via-orange-50/40 to-yellow-50/50 border-amber-300/40 shadow-sm",
                !isCurrentDay && isPastDay && "bg-gray-50/50 opacity-60",
                !isCurrentDay && !isPastDay && "hover:bg-gradient-to-br hover:from-blue-50/30 hover:to-purple-50/30 hover:shadow-md",
                (dayOfWeek === 0 || dayOfWeek === 6) && !isCurrentDay && "bg-gradient-to-b from-gray-50/30 to-gray-100/20"
              )}
            >
              {/* 호버 효과를 위한 배경 */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/0 group-hover:from-white/20 group-hover:to-blue-50/20 transition-all duration-300 pointer-events-none" />
              
              {/* 날짜 헤더 */}
              <div className="flex items-start justify-between mb-1 md:mb-2 relative z-10">
                <div className={cn(
                  "text-xs md:text-sm lg:text-base font-bold transition-all duration-300",
                  isCurrentDay && "bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full w-7 h-7 md:w-8 md:h-8 flex items-center justify-center shadow-md ring-2 ring-amber-400/30 ring-offset-2 ring-offset-white",
                  !isCurrentDay && isPastDay && "text-gray-400",
                  !isCurrentDay && !isPastDay && dayOfWeek === 0 && "text-red-500 font-extrabold",
                  !isCurrentDay && !isPastDay && dayOfWeek === 6 && "text-blue-500 font-extrabold",
                  !isCurrentDay && !isPastDay && dayOfWeek !== 0 && dayOfWeek !== 6 && "text-gray-700"
                )}>
                  {format(day, 'd')}
                </div>
                
                {/* 투어 개수 배지 */}
                {dayTours.length > 0 && !isPastDay && (
                  <div className="flex items-center gap-1">
                    {recruitingTours.length > 0 && (
                      <div className="flex items-center justify-center w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full text-white text-[9px] md:text-[10px] font-bold shadow-sm">
                        {recruitingTours.length}
                      </div>
                    )}
                    {confirmedTours.length > 0 && (
                      <div className="flex items-center justify-center w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-blue-400 to-violet-500 rounded-full text-white text-[9px] md:text-[10px] font-bold shadow-sm">
                        {confirmedTours.length}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 투어 카드 (반응형 + 그룹핑) */}
              {dayTours.length > 0 && !isPastDay && (
                <div className="space-y-1 relative z-10">
                  {/* 확장 여부 확인 */}
                  {(() => {
                    const isExpanded = expandedDates.has(dateKey);
                    const toursToShow = isExpanded ? dayTours : dayTours.slice(0, 2);
                    
                    return (
                      <>
                        {/* 투어 표시 */}
                        {toursToShow.map((tour) => {
                    const status = getTourStatus(tour);
                    const statusStyle = getTourStatusStyle(status);
                    const trailStyle = getTrailStyle(tour.course_name);
                    
                          return (
                            <Link
                              key={tour.trip_id}
                              to={`/product-detail/${tour.course_id}`}
                              className="block transform transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 hover:z-20"
                            >
                              <div className={cn(
                                "relative text-[10px] md:text-[11px] px-2 py-1 md:px-2.5 md:py-1.5 rounded-lg cursor-pointer transition-all",
                                statusStyle.container,
                                statusStyle.glow
                              )}>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs hidden md:inline">{statusStyle.icon}</span>
                                  <div className="flex-1 font-semibold truncate leading-tight text-gray-800">
                                    {/* 모바일에서는 짧게 */}
                                    <span className="md:hidden">
                                      {tour.course_name.split(' ')[0]}
                                    </span>
                                    <span className="hidden md:inline">
                                      {tour.course_name.split(' ').slice(0, 2).join(' ')}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* 인원 표시 */}
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Users className="w-2.5 h-2.5 text-gray-600" />
                                  <span className="text-[9px] text-gray-600">
                                    {tour.max_participants - tour.available_seats}/{tour.max_participants}
                                  </span>
                                  {status === 'recruiting-urgent' && (
                                    <span className="text-[9px] font-bold text-orange-600 animate-pulse">
                                      ⚡{tour.available_seats}석
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                        
                        {/* 더보기/접기 버튼 */}
                        {dayTours.length > 2 && (
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleDateExpansion(dateKey);
                            }}
                            className="w-full text-[9px] md:text-[10px] text-gray-600 text-center font-semibold bg-gradient-to-r from-gray-100/80 to-gray-200/80 hover:from-gray-200/90 hover:to-gray-300/90 rounded-lg px-2 py-0.5 md:py-1 shadow-sm border border-gray-300/40 transition-all duration-300 hover:scale-105"
                          >
                            {isExpanded ? (
                              <span>접기</span>
                            ) : (
                              <>
                                <span className="md:hidden">+{dayTours.length - 2}</span>
                                <span className="hidden md:inline">+{dayTours.length - 2}개 더보기</span>
                              </>
                            )}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              
              {/* 투어가 없는 날 */}
              {dayTours.length === 0 && !isPastDay && (
                <div className="flex items-center justify-center h-full min-h-[40px] md:min-h-[60px]">
                  <div className="text-[9px] md:text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    투어 없음
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompactTourCalendar;